import { Button } from "@ui/Button";
import { Slider } from "@ui/Slider";
import {
	createEffect,
	createMemo,
	createSignal,
	onCleanup,
	onMount,
	Show,
	type Accessor,
} from "solid-js";
import "./VolumeControl.css";
import {
	createVideoPreviewOriginatorId,
	dispatchVideoPreviewVolumeChanged,
	readStoredVideoMuted,
	readStoredVideoVolume,
	storeVideoPreviewVolume,
	VIDEO_PREVIEW_VOLUME_CHANGED_EVENT,
	type VideoPreviewVolumeChangedDetail,
} from "./videoPreviewVolume";

type MaybeAccessor<T> = T | Accessor<T | undefined>;

const resolveMaybeAccessor = <T,>(
	value: MaybeAccessor<T> | undefined,
): T | undefined =>
	typeof value === "function" ? (value as Accessor<T | undefined>)() : value;

type VolumeControlProps = {
	/** Pass VideoPreview's id when embedded; omit for standalone controls. */
	originatorId?: string;
	getVideoElement?: () => HTMLVideoElement | undefined;
	forceMuteAudio?: MaybeAccessor<boolean>;
	disabled?: MaybeAccessor<boolean>;
	/** Reuse hook state when embedding alongside a video element (e.g. VideoPreview). */
	controls?: ReturnType<typeof useVolumeControl>;
};

export const useVolumeControl = (props: VolumeControlProps) => {
	const originatorId = props.originatorId ?? createVideoPreviewOriginatorId();
	const forceMuteAudio = () => resolveMaybeAccessor(props.forceMuteAudio) ?? false;
	const disabled = () => resolveMaybeAccessor(props.disabled) ?? false;

	const [videoMuted, setVideoMuted] = createSignal(
		forceMuteAudio() ? true : readStoredVideoMuted(),
	);
	/** 0–1; `video.volume` while unmuted; when muted UI can show 0 without clearing this. */
	const [videoVolumeLevel, setVideoVolumeLevel] = createSignal(
		readStoredVideoVolume(),
	);

	const volumeSliderPercent = createMemo(() =>
		Math.round(videoVolumeLevel() * 100),
	);

	const panelId = `${originatorId}-volume-panel`;
	const sliderId = `${originatorId}-volume`;

	const broadcastVolumeChange = (volume: number, muted: boolean) => {
		dispatchVideoPreviewVolumeChanged({
			originator: originatorId,
			volume,
			muted,
		});
	};

	const applyVolumeFromSlider = (percent: number) => {
		if (disabled() || forceMuteAudio()) {
			return;
		}
		const el = props.getVideoElement?.();
		const v = Math.max(0, Math.min(1, percent / 100));
		if (el) {
			el.volume = v;
		}
		setVideoVolumeLevel(v);
		const muted = v === 0;
		if (el) {
			el.muted = muted;
		}
		setVideoMuted(muted);
		broadcastVolumeChange(v, muted);
	};

	const toggleMute = () => {
		if (disabled() || forceMuteAudio()) {
			return;
		}
		const el = props.getVideoElement?.();
		if (el?.muted || el?.volume === 0 || (!el && videoMuted())) {
			const restoredVolume = videoVolumeLevel() > 0 ? videoVolumeLevel() : 1;
			if (el) {
				el.volume = restoredVolume;
				el.muted = false;
			}
			setVideoVolumeLevel(restoredVolume);
			setVideoMuted(false);
			broadcastVolumeChange(restoredVolume, false);
			return;
		}

		if (el) {
			el.muted = true;
			setVideoMuted(true);
			broadcastVolumeChange(el.volume, true);
			return;
		}

		setVideoMuted(true);
		broadcastVolumeChange(videoVolumeLevel(), true);
	};

	const applyExternalVolume = (volume: number, muted: boolean) => {
		const el = props.getVideoElement?.();
		if (el) {
			el.volume = volume;
			el.muted = muted;
		}
		setVideoVolumeLevel(volume);
		setVideoMuted(muted);
	};

	const attachToVideo = (el: HTMLVideoElement) => {
		const onVolumeChange = () => {
			setVideoMuted(el.muted);
			setVideoVolumeLevel(el.volume);
			if (!forceMuteAudio()) {
				storeVideoPreviewVolume(el.volume, el.muted);
			}
		};

		el.volume = videoVolumeLevel();
		el.muted = videoMuted();
		setVideoMuted(el.muted);
		setVideoVolumeLevel(el.volume);
		el.addEventListener("volumechange", onVolumeChange);

		return () => {
			el.removeEventListener("volumechange", onVolumeChange);
		};
	};

	onMount(() => {
		const onVolumeChanged = (evt: Event) => {
			const e = evt as CustomEvent<VideoPreviewVolumeChangedDetail>;
			if (e.detail.originator === originatorId || forceMuteAudio()) {
				return;
			}
			applyExternalVolume(e.detail.volume, e.detail.muted);
		};
		document.addEventListener(
			VIDEO_PREVIEW_VOLUME_CHANGED_EVENT,
			onVolumeChanged,
		);
		onCleanup(() => {
			document.removeEventListener(
				VIDEO_PREVIEW_VOLUME_CHANGED_EVENT,
				onVolumeChanged,
			);
		});
	});

	createEffect(() => {
		const forced = forceMuteAudio();
		queueMicrotask(() => {
			const el = props.getVideoElement?.();
			if (forced) {
				if (el) {
					el.muted = true;
				}
				setVideoMuted(true);
				return;
			}

			const volume = readStoredVideoVolume();
			const muted = readStoredVideoMuted();
			if (el) {
				el.volume = volume;
				el.muted = muted;
			}
			setVideoVolumeLevel(volume);
			setVideoMuted(muted);
		});
	});

	return {
		videoMuted,
		volumeSliderPercent,
		applyVolumeFromSlider,
		toggleMute,
		attachToVideo,
		panelId,
		sliderId,
		forceMuteAudio,
		disabled,
		originatorId,
	};
};

export const VolumeControl = (props: VolumeControlProps) => {
	const volume = props.controls ?? useVolumeControl(props);
	const [panelOpen, setPanelOpen] = createSignal(false);
	let closeTimer: ReturnType<typeof setTimeout> | undefined;

	const clearCloseTimer = () => {
		if (closeTimer !== undefined) {
			clearTimeout(closeTimer);
			closeTimer = undefined;
		}
	};

	const openPanel = () => {
		if (volume.forceMuteAudio()) {
			return;
		}
		clearCloseTimer();
		setPanelOpen(true);
	};

	const scheduleClosePanel = () => {
		clearCloseTimer();
		closeTimer = setTimeout(() => {
			setPanelOpen(false);
			closeTimer = undefined;
		}, 200);
	};

	const handleFocusOut = (e: FocusEvent) => {
		const current = e.currentTarget;
		const next = e.relatedTarget;
		if (!(current instanceof HTMLElement)) {
			return;
		}
		if (next instanceof Node && current.contains(next)) {
			return;
		}
		clearCloseTimer();
		setPanelOpen(false);
	};

	onCleanup(clearCloseTimer);

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: volume hover zone wraps button + slider
		<div
			class="gvtui-volume-control"
			classList={{
				"gvtui-volume-control--disabled": volume.forceMuteAudio(),
			}}
			onPointerDown={(e) => e.stopPropagation()}
			onMouseLeave={scheduleClosePanel}
			onFocusOut={handleFocusOut}
		>
			<Show when={panelOpen()}>
				<div class="gvtui-volume-control__hover-bridge" aria-hidden="true" />
				<div class="gvtui-volume-control__panel" id={volume.panelId}>
					<Slider
						id={volume.sliderId}
						min={0}
						max={100}
						step={1}
						orientation="vertical"
						theme="white"
						value={volume.volumeSliderPercent}
						disabled={volume.forceMuteAudio()}
						aria-label="Volume"
						onChange={(v) => {
							volume.applyVolumeFromSlider(v);
						}}
					/>
				</div>
			</Show>
			<Button
				type="button"
				variant="text"
				theme="black"
				shape="circle"
				class="gvtui-volume-control__button"
				disabled={volume.forceMuteAudio()}
				symbol={volume.videoMuted() ? "volume_off" : "volume_up"}
				symbolRelativeSize="even-larger"
				aria-label="Volume"
				aria-expanded={panelOpen()}
				aria-controls={panelOpen() ? volume.panelId : undefined}
				onMouseEnter={openPanel}
				onFocus={openPanel}
				onClick={(e) => {
					e.stopPropagation();
					volume.toggleMute();
				}}
			/>
		</div>
	);
};
